import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

import Blog from "./blog";
import Projects from "./projects";
import About from "./about";
import Skills from "./skills";
import Education from "./education";
import Contact from "./contact";

import baseUrl from "../components/baseurl";

import { FaArrowUp, FaGithub, FaLinkedin } from "react-icons/fa";

const MainPage = () => {
  const [visitorIp, setVisitorIp] = useState();
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const blogSectionRef = useRef(null);
  const projectsSectionRef = useRef(null);
  const aboutSectionRef = useRef(null);
  const skillsSectionRef = useRef(null);
  const educationSectionRef = useRef(null);
  const contactSectionRef = useRef(null);

  const scrollToSection = (sectionRef) => {
    window.scrollTo({
      top: sectionRef.current.offsetTop,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollToTop(window.scrollY > 400);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  useEffect(() => {
    const fetchVisitorIp = async () => {
      try {
        const response = await axios.get(`https://api.ipify.org?format=json`);
        setVisitorIp(response.data.ip);
      } catch (error) {
        console.error("Failed to fetch IP:", error);
      }
    };

    fetchVisitorIp();
  }, []);

  useEffect(() => {
    const recordVisitorData = async () => {
      try {
        await axios.post(`${baseUrl}/ipdata`, { ipdata: visitorIp });
        console.log("Visitor's data recorded");
      } catch (error) {
        console.error("Failed to record visitor data:", error);
      }
    };

    if (visitorIp) {
      recordVisitorData();
    }
  }, [visitorIp]);

  return (
    <div className="font-sans text-gray-800 bg-gray-50">
      {showScrollToTop && (
        <button
          onClick={scrollToTop}
          className="fixed right-6 bottom-10 p-3 bg-indigo-600 hover:bg-indigo-700 transition-colors duration-300 text-white rounded-full z-50 shadow-lg flex items-center justify-center"
          aria-label="Scroll to top"
        >
          <FaArrowUp />
        </button>
      )}

      <header className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-800 to-indigo-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute h-96 w-96 rounded-full bg-indigo-400 -top-20 -left-20 animate-pulse"></div>
          <div className="absolute h-64 w-64 rounded-full bg-blue-400 bottom-20 right-10 animate-pulse delay-700"></div>
          <div className="absolute h-32 w-32 rounded-full bg-purple-400 bottom-40 left-1/2 animate-pulse delay-1000"></div>
        </div>

        <nav className="flex flex-row justify-between items-center py-6 px-6 md:px-10 lg:px-20 backdrop-blur-sm bg-opacity-20 bg-indigo-900 sticky top-0 z-40">
          <div className="flex items-center">
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-200 to-white">
              Ayobami Isaac
            </h1>
          </div>

          <div className="hidden md:flex items-center space-x-8">
            <button
              className="nav-link relative text-sm tracking-wider uppercase font-medium hover:text-blue-200 transition-colors duration-300 after:content-[''] after:absolute after:w-0 after:h-0.5 after:bg-blue-200 after:left-0 after:-bottom-1 after:transition-all hover:after:w-full"
              onClick={() => scrollToSection(aboutSectionRef)}
            >
              About Me
            </button>
            <button
              className="nav-link relative text-sm tracking-wider uppercase font-medium hover:text-blue-200 transition-colors duration-300 after:content-[''] after:absolute after:w-0 after:h-0.5 after:bg-blue-200 after:left-0 after:-bottom-1 after:transition-all hover:after:w-full"
              onClick={() => scrollToSection(skillsSectionRef)}
            >
              Skills
            </button>
            <button
              className="nav-link relative text-sm tracking-wider uppercase font-medium hover:text-blue-200 transition-colors duration-300 after:content-[''] after:absolute after:w-0 after:h-0.5 after:bg-blue-200 after:left-0 after:-bottom-1 after:transition-all hover:after:w-full"
              onClick={() => scrollToSection(projectsSectionRef)}
            >
              Projects
            </button>
            <button
              className="nav-link relative text-sm tracking-wider uppercase font-medium hover:text-blue-200 transition-colors duration-300 after:content-[''] after:absolute after:w-0 after:h-0.5 after:bg-blue-200 after:left-0 after:-bottom-1 after:transition-all hover:after:w-full"
              onClick={() => scrollToSection(blogSectionRef)}
            >
              Blog
            </button>
            <button
              className="nav-link relative text-sm tracking-wider uppercase font-medium hover:text-blue-200 transition-colors duration-300 after:content-[''] after:absolute after:w-0 after:h-0.5 after:bg-blue-200 after:left-0 after:-bottom-1 after:transition-all hover:after:w-full"
              onClick={() => scrollToSection(contactSectionRef)}
            >
              Contact
            </button>
          </div>

          <div className="md:hidden">
            <button
              onClick={toggleMobileMenu}
              className="p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200"
              aria-label="Toggle menu"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {isMobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </nav>

        {isMobileMenuOpen && (
          <>
            <div
              className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm"
              onClick={toggleMobileMenu}
            />

            <div className="fixed top-0 right-0 h-full w-80 max-w-[85vw] z-50 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out">
              <div className="bg-gradient-to-r from-indigo-600 to-blue-600 p-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-white">Menu</h3>
                  <button
                    onClick={toggleMobileMenu}
                    className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors duration-200"
                    aria-label="Close menu"
                  >
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                <div className="p-6 space-y-2">
                  <button
                    className="w-full px-4 py-3 text-left text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all duration-200 flex items-center"
                    onClick={() => {
                      toggleMobileMenu();
                      scrollToSection(aboutSectionRef);
                    }}
                  >
                    <span className="text-lg font-medium">About Me</span>
                  </button>

                  <button
                    className="w-full px-4 py-3 text-left text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all duration-200 flex items-center"
                    onClick={() => {
                      toggleMobileMenu();
                      scrollToSection(skillsSectionRef);
                    }}
                  >
                    <span className="text-lg font-medium">Skills</span>
                  </button>

                  <button
                    className="w-full px-4 py-3 text-left text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all duration-200 flex items-center"
                    onClick={() => {
                      toggleMobileMenu();
                      scrollToSection(projectsSectionRef);
                    }}
                  >
                    <span className="text-lg font-medium">Projects</span>
                  </button>

                  <button
                    className="w-full px-4 py-3 text-left text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all duration-200 flex items-center"
                    onClick={() => {
                      toggleMobileMenu();
                      scrollToSection(blogSectionRef);
                    }}
                  >
                    <span className="text-lg font-medium">Blog</span>
                  </button>

                  <button
                    className="w-full px-4 py-3 text-left text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all duration-200 flex items-center"
                    onClick={() => {
                      toggleMobileMenu();
                      scrollToSection(educationSectionRef);
                    }}
                  >
                    <span className="text-lg font-medium">Education</span>
                  </button>

                  <Link
                    to="/about-web"
                    className="block w-full px-4 py-3 text-left text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all duration-200"
                    onClick={toggleMobileMenu}
                  >
                    <span className="text-lg font-medium">About Website</span>
                  </Link>
                </div>

                <div className="px-6 py-4">
                  <div className="h-px bg-gray-200"></div>
                </div>

                <div className="px-6 pb-6">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                    Connect with me
                  </h4>
                  <div className="flex space-x-3">
                    <a
                      href="https://github.com/ayoadeoye1"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors duration-200"
                    >
                      <FaGithub size={20} className="text-gray-700" />
                    </a>
                    <a
                      href="https://linkedin.com/in/ayoadeoye"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors duration-200"
                    >
                      <FaLinkedin size={20} className="text-gray-700" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        <div className="container mx-auto px-6 md:px-10 pt-20 pb-32 flex flex-col md:flex-row items-center justify-between">
          <div className="md:w-1/2 md:pr-10 text-center md:text-left mb-12 md:mb-0">
            <div className="animate-fadeIn">
              <h2 className="font-bold text-3xl md:text-4xl lg:text-5xl leading-tight mb-4">
                <span className="block">Hi, I'm Ayobami Isaac</span>
                <span className="block mt-2 text-blue-300">
                  Full Stack Developer
                </span>
              </h2>

              <p className="text-blue-100 text-lg md:text-xl max-w-xl mx-auto md:mx-0 mt-6">
                I build exceptional and scalable web applications using modern
                technologies. Passionate about creating elegant solutions to
                complex problems.
              </p>

              <div className="mt-10 flex flex-wrap gap-4 justify-center md:justify-start relative z-10">
                <a
                  href="/assets/resume.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 transition-colors duration-300 rounded-lg text-white font-medium shadow-lg hover:shadow-xl flex items-center justify-center cursor-pointer"
                >
                  Download Resume
                </a>
                <button
                  onClick={() => scrollToSection(contactSectionRef)}
                  className="px-6 py-3 bg-transparent hover:bg-blue-800 transition-colors duration-300 border-2 border-blue-400 rounded-lg text-white font-medium flex items-center justify-center cursor-pointer"
                >
                  Contact Me
                </button>
              </div>

              <div className="mt-10 flex justify-center md:justify-start space-x-6 relative z-10">
                <a
                  href="https://github.com/ayoadeoye1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-blue-300 transition-colors cursor-pointer p-2"
                >
                  <FaGithub size={24} />
                </a>
                <a
                  href="https://linkedin.com/in/ayoadeoye"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-blue-300 transition-colors cursor-pointer p-2"
                >
                  <FaLinkedin size={24} />
                </a>
              </div>
            </div>
          </div>

          <div className="md:w-1/2 relative">
            <div className="animate-float relative z-10 mx-auto">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full opacity-20 blur-xl transform scale-110"></div>
                <img
                  className="relative z-10 rounded-full h-64 w-64 md:h-80 md:w-80 object-cover mx-auto shadow-xl border-4 border-blue-100/20"
                  src="/assets/ay.png"
                  alt="Ayobami Isaac"
                />
              </div>
            </div>

            <div className="absolute top-0 right-0 h-20 w-20 bg-blue-400 rounded-full opacity-20 animate-pulse delay-300"></div>
            <div className="absolute bottom-10 left-10 h-16 w-16 bg-indigo-400 rounded-full opacity-20 animate-ping delay-700"></div>
          </div>
        </div>
      </header>

      <div className="bg-gray-50">
        <section ref={aboutSectionRef} className="py-24 bg-white">
          <About />
        </section>

        <section ref={skillsSectionRef} className="py-24 bg-gray-50">
          <Skills />
        </section>

        <section ref={projectsSectionRef} className="py-24 bg-white">
          <Projects />
        </section>

        <section ref={blogSectionRef} className="py-24 bg-gray-50">
          <Blog />
        </section>

        <section ref={educationSectionRef} className="py-24 bg-white">
          <Education />
        </section>

        <section
          id="contact"
          ref={contactSectionRef}
          className="py-24 bg-indigo-900 text-white"
        >
          <Contact />
        </section>
      </div>
    </div>
  );
};

export default MainPage;
